/**
 * STAIR BUILDER (docs/bim-plan/33-stair-builder.md, Matt 2026-10-10): a stair is a CONFIGURATION,
 * never a drawing. Its recipe — runs joined by turns — is laid out exactly here, with nothing
 * inferred from a drawn line.
 *
 *   bottom · run · turn · run · turn · run · top
 *
 * Conventions:
 * - The stair's `path` is only its PLACEMENT: node 0 is the TOP NOSING's centre and node 0 → 1 the
 *   direction the top run climbs. The top stays put (Matt: "it's usually the less flexible
 *   datum"), so every resize grows the stair downhill; moving / rotating the stair moves the path.
 * - Every tread position below the top riser is a tread, a landing, or a winder: a landing IS a
 *   step (one riser up from the last tread, one riser down to the next) — there is no tread flush
 *   with a landing. So risers = treads + landings + winders + 1.
 * - A flight never climbs more than 12'-7" (CRC R311.7.3): a run that would is split with a
 *   landing automatically (Matt: "add it automatically to keep us in compliance").
 * - Output is the same StairLayout every consumer already reads (3D, plan, pick, guards, code).
 */
import type { Pt2 } from './types'
import { stairRun } from './stair'
import type { StairLayout, StairLayoutLeg, StairLayoutOpts } from './stairLayout'
import { stairSweepLayout, type StairSweepPiece } from './stairSweep'

export type StairTurnKind = 'landing' | 'winders'
export interface StairRecipeTurn {
  kind: StairTurnKind
  /** the way the walker going up turns */
  dir: 'left' | 'right'
  /** 0 = a straight landing (no turn), 90, 180 */
  angle: 0 | 90 | 180
  /** landing depth along the incoming run (null = the least the turn allows) */
  depthFt?: number | null
  /** a 180° turn: the clear gap between the two runs (the well) */
  gapFt?: number | null
  /** winders: treads in the turn */
  winders?: number | null
  /** winders on an as-built stair: how far the fan point sits out past the inner corner (null =
   *  the least that meets code, as on any stair) */
  winderOffsetFt?: number | null
  /** added by the solver (a run past 12'-7") — not part of the stored recipe */
  auto?: boolean
  /** solver only: the index of the recipe turn this came from (none for an automatic landing) */
  src?: number
}
export interface StairRecipeRun {
  /** treads in this run (null = the solver shares the rest out) */
  treads?: number | null
  /** this run's width (null = the stair's) */
  widthFt?: number | null
}
export interface StairRecipe {
  config: string
  runs: StairRecipeRun[]
  turns: StairRecipeTurn[]
  /** the CURVED family (stairSweep.ts): the centreline's pieces, bottom → top — set, the stair
   *  follows them instead of runs and turns */
  sweep?: StairSweepPiece[]
  /** BRANCHING stairs: 'split' = one run up to a landing, two arms on from its sides (runs =
   *  [main, each arm]); 'join' = two arms up to a landing, one run on (runs = [each arm, main]).
   *  The one turn is the landing (90° — the arms leave sideways — or 180° — they come back
   *  alongside the main run, the imperial stair). */
  branch?: 'split' | 'join'
  /** AS BUILT (Matt 2026-10-10: "when we're modeling existing stairs that may not comply with code
   *  we need flexibility to break the rules"): nothing is added or sized for code — no automatic
   *  mid-flight landing, landings as shallow as set, winders as tight as set (`winderOffsetFt`).
   *  The code check still reports every rule the stair breaks. Set on stairs placed in the
   *  Existing phase. */
  asBuilt?: boolean
}

const IN = 1 / 12
export const STAIR_MAX_FLIGHT_RISE_FT = 151 * IN
export const STAIR_MIN_LANDING_FT = 36 * IN

const L = (angle: 0 | 90 | 180, dir: 'left' | 'right' = 'left', extra: Partial<StairRecipeTurn> = {}): StairRecipeTurn => ({ kind: 'landing', dir, angle, ...extra })
const W = (angle: 90 | 180, n: number, dir: 'left' | 'right' = 'left'): StairRecipeTurn => ({ kind: 'winders', dir, angle, winders: n })
const runs = (n: number, set: Record<number, number> = {}): StairRecipeRun[] => Array.from({ length: n }, (_, i) => (i in set ? { treads: set[i] } : {}))

/**
 * The catalog the Stair tool offers (bim-plan 33 §2). `ready` = laid out by this engine today;
 * the others are listed so the menu can show them as coming.
 */
export const STAIR_CONFIGS: { id: string; label: string; note: string; ready: boolean; recipe: () => StairRecipe }[] = [
  { id: 'straight', label: 'Straight', note: 'One run', ready: true, recipe: () => ({ config: 'straight', runs: runs(1), turns: [] }) },
  { id: 'straightLanding', label: 'Straight with a landing', note: 'Two runs, a landing between', ready: true, recipe: () => ({ config: 'straightLanding', runs: runs(2), turns: [L(0)] }) },
  { id: 'lLanding', label: 'L with a landing', note: 'A quarter turn on a landing', ready: true, recipe: () => ({ config: 'lLanding', runs: runs(2), turns: [L(90)] }) },
  { id: 'lWinders', label: 'L with winders', note: 'A quarter turn on three winders', ready: true, recipe: () => ({ config: 'lWinders', runs: runs(2), turns: [W(90, 3)] }) },
  { id: 'uLanding', label: 'U with a landing', note: 'A half turn on one landing', ready: true, recipe: () => ({ config: 'uLanding', runs: runs(2), turns: [L(180, 'left', { gapFt: 4 * IN })] }) },
  { id: 'uSplit', label: 'U with a split landing', note: 'Two quarter landings, a short run between', ready: true, recipe: () => ({ config: 'uSplit', runs: runs(3, { 1: 1 }), turns: [L(90), L(90)] }) },
  { id: 'uWinders', label: 'U with winders', note: 'A half turn on six winders', ready: true, recipe: () => ({ config: 'uWinders', runs: runs(2), turns: [W(180, 6)] }) },
  { id: 'uLandingWinders', label: 'U, landing and winders', note: 'A quarter landing, then three winders', ready: true, recipe: () => ({ config: 'uLandingWinders', runs: runs(3, { 1: 1 }), turns: [L(90), W(90, 3)] }) },
  { id: 'doubleL', label: 'Double L (Z)', note: 'A turn one way, then the other', ready: true, recipe: () => ({ config: 'doubleL', runs: runs(3), turns: [L(90, 'left'), L(90, 'right')] }) },
  { id: 'threeQuarter', label: 'Three-quarter turn', note: 'Three quarter landings', ready: true, recipe: () => ({ config: 'threeQuarter', runs: runs(4), turns: [L(90), L(90), L(90)] }) },
  { id: 'windersBottom', label: 'Winders at the bottom', note: 'A turn at the start only', ready: true, recipe: () => ({ config: 'windersBottom', runs: runs(2, { 0: 0 }), turns: [W(90, 3)] }) },
  { id: 'windersTop', label: 'Winders at the top', note: 'A turn at the arrival only', ready: true, recipe: () => ({ config: 'windersTop', runs: runs(2, { 1: 0 }), turns: [W(90, 3)] }) },
  { id: 'split', label: 'Split (T) stair', note: 'One run up, two runs off the landing', ready: true, recipe: () => ({ config: 'split', runs: runs(2), turns: [L(90)], branch: 'split' }) },
  { id: 'imperial', label: 'Imperial (split back)', note: 'One run up the middle, two runs back up either side', ready: true, recipe: () => ({ config: 'imperial', runs: runs(2), turns: [L(180, 'left', { gapFt: 4 * IN })], branch: 'split' }) },
  { id: 'bifurcated', label: 'Bifurcated', note: 'Two runs up to a landing, one run on', ready: true, recipe: () => ({ config: 'bifurcated', runs: runs(2), turns: [L(90)], branch: 'join' }) },
  { id: 'curved', label: 'Curved', note: 'One curved run', ready: true, recipe: () => ({ config: 'curved', runs: runs(1), turns: [], sweep: [{ kind: 'arc', dir: 'left', sweepDeg: 90, r0: 7 }] }) },
  { id: 'spiral', label: 'Spiral', note: 'Treads around a column', ready: false, recipe: () => ({ config: 'spiral', runs: runs(1), turns: [] }) },
  { id: 'helical', label: 'Helical', note: 'Open centre, curved stringers', ready: false, recipe: () => ({ config: 'helical', runs: runs(1), turns: [] }) },
  { id: 'sculpted', label: 'Sculpted (free curve)', note: 'A free walk line, no post, plaster sides', ready: true, recipe: () => ({ config: 'sculpted', runs: runs(1), turns: [], sweep: [{ kind: 'arc', dir: 'right', sweepDeg: 50, r0: 8 }, { kind: 'arc', dir: 'left', sweepDeg: 70, r0: 8 }] }) },
  { id: 'runSpiral', label: 'Run into an open spiral', note: 'A straight run winding on into a spiral', ready: true, recipe: () => ({ config: 'runSpiral', runs: runs(1), turns: [], sweep: [{ kind: 'line', lenFt: 6 }, { kind: 'arc', dir: 'left', sweepDeg: 180, r0: 8, r1: 4.5 }] }) },
]

/** the stored recipe off a stair's params (null = not a built stair) */
export function stairRecipeOf(params: Record<string, unknown> | undefined): StairRecipe | null {
  const r = params?.stairRecipe as StairRecipe | undefined
  if (!r || typeof r !== 'object' || !Array.isArray(r.runs) || !Array.isArray(r.turns)) return null
  if (r.runs.length !== r.turns.length + 1) return null
  return r
}

/** a fresh recipe for a catalog configuration */
export function stairRecipeFor(config: string): StairRecipe | null {
  const c = STAIR_CONFIGS.find((x) => x.id === config)
  return c ? c.recipe() : null
}

/** the tread positions a turn uses (a landing is one step; winders one each) */
const turnCost = (t: StairRecipeTurn) => (t.kind === 'winders' ? Math.max(2, Math.round(t.winders ?? 3)) : 1)

/**
 * Share the stair's tread positions among the runs: set runs keep theirs, the rest split evenly,
 * any odd tread going to the LOWER runs (the top stays put); too few to go round comes off the
 * bottom first. Then split any flight past 12'-7" with a landing, and share again.
 */
export function solveStairRecipe(recipe: StairRecipe, positions: number, riserFt: number): { runs: { treads: number; widthFt: number | null }[]; turns: StairRecipeTurn[] } {
  const rs: StairRecipeRun[] = recipe.runs.map((r) => ({ ...r }))
  const ts: StairRecipeTurn[] = recipe.turns.map((t, i) => ({ ...t, src: i }))
  // a half turn on winders is two quarter turns of winders back to back
  for (let i = ts.length - 1; i >= 0; i--) {
    const t = ts[i]
    if (t.kind !== 'winders' || t.angle !== 180) continue
    const m = Math.max(4, Math.round(t.winders ?? 6))
    const a = Math.ceil(m / 2), b = m - a
    ts.splice(i, 1, { ...t, angle: 90, winders: a }, { ...t, angle: 90, winders: b })
    rs.splice(i + 1, 0, { treads: 0, widthFt: rs[i].widthFt })
  }
  const share = () => {
    const cost = ts.reduce((a, t) => a + turnCost(t), 0)
    let left = Math.max(0, positions - cost)
    const out = rs.map((r) => (typeof r.treads === 'number' && r.treads >= 0 ? Math.round(r.treads) : -1))
    const fixed = out.reduce((a, v) => a + (v >= 0 ? v : 0), 0)
    const autos = out.map((v, i) => (v < 0 ? i : -1)).filter((i) => i >= 0)
    if (autos.length) {
      left -= fixed
      if (left < 0) {
        // set runs ask for more than there is: take it off the lowest set runs
        let over = -left
        for (let i = 0; i < out.length && over > 0; i++) if (out[i] > 0) { const d = Math.min(out[i], over); out[i] -= d; over -= d }
        left = 0
      }
      const each = Math.floor(left / autos.length)
      let extra = left - each * autos.length
      for (const i of autos) { out[i] = each + (extra > 0 ? 1 : 0); if (extra > 0) extra-- }
    } else {
      // every run set: the bottom run absorbs the difference
      let diff = left - fixed
      for (let i = 0; i < out.length && diff !== 0; i++) {
        const d = diff > 0 ? diff : Math.max(diff, -out[i])
        out[i] += d
        diff -= d
      }
    }
    return out
  }
  let out = share()
  for (let guard = 0; guard < 8 && !recipe.asBuilt; guard++) {
    // a flight runs from a floor or landing to the next: its runs' treads, the winders between
    // them (winders are part of a flight), and the riser up to the next landing / floor
    let bad = -1
    for (let i = 0, start = 0, sum = 0; i < out.length; i++) {
      sum += out[i]
      const t = ts[i]
      if (t && t.kind === 'winders') { sum += turnCost(t); continue }
      if ((sum + 1) * riserFt > STAIR_MAX_FLIGHT_RISE_FT + 1e-6) {
        // split the longest run of this flight
        bad = start
        for (let j = start; j <= i; j++) if (out[j] > out[bad]) bad = j
        break
      }
      start = i + 1; sum = 0
    }
    if (bad < 0) break
    rs.splice(bad, 1, { widthFt: rs[bad].widthFt }, { widthFt: rs[bad].widthFt })
    ts.splice(bad, 0, { kind: 'landing', dir: 'left', angle: 0, auto: true })
    out = share()
  }
  return { runs: out.map((t, i) => ({ treads: t, widthFt: rs[i].widthFt ?? null })), turns: ts }
}

type V = Pt2
const add = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1]]
const mul = (a: V, k: number): V => [a[0] * k, a[1] * k]
const leftN = (u: V): V => [-u[1], u[0]]

/**
 * Lay a built stair out. `anchor` = the top nosing's centre, `dir` = the top run's climbing
 * direction (the stair's path, node 0 → 1).
 */
export function stairRecipeLayout(recipe: StairRecipe, anchor: Pt2, dir: Pt2, opts: StairLayoutOpts): StairLayout | null {
  if (recipe.sweep?.length) return stairSweepLayout(recipe.sweep, anchor, dir, opts, !!recipe.asBuilt)
  if (recipe.branch && recipe.runs.length === 2 && recipe.turns.length === 1) return branchLayout(recipe, anchor, dir, opts)
  const calc = stairRun(opts.floorToFloorFt, opts.maxRiserFt, opts.treadDepthFt)
  const r = calc.riserFt, td = opts.treadDepthFt
  const solved = solveStairRecipe(recipe, calc.treads, r)
  const W0 = opts.widthFt
  const wOf = (i: number) => solved.runs[i].widthFt || W0

  // ---- bottom-up in a local frame: the first riser's centre at the origin, climbing +x ----
  const legs: StairLayoutLeg[] = []
  const treads: StairLayout['treads'] = []
  const landings: StairLayout['landings'] = []
  const riserLines: StairLayout['riserLines'] = []
  const sideLines: StairLayout['sideLines'] = []
  const chainL: V[] = [], chainR: V[] = []
  const winders: NonNullable<StairLayout['winders']> = []
  const zones: NonNullable<StairLayout['winderZones']> = []
  const usedPts: V[] = []
  let P: V = [0, 0], u: V = [1, 0], k = 0
  for (let i = 0; i < solved.runs.length; i++) {
    const nT = solved.runs[i].treads
    const w = wOf(i)
    const n = leftN(u)
    const z0 = k * r
    const at = (s: number, c: number): V => add(add(P, mul(u, s)), mul(n, c))
    for (let j = 0; j < nT; j++) {
      const sF = j * td, sB = sF + td
      treads.push({ i: k + j, quad: [at(sF, -w / 2), at(sB, -w / 2), at(sB, w / 2), at(sF, w / 2)], zTop: (k + j + 1) * r })
      riserLines.push({ pts: [at(sF, -w / 2), at(sF, w / 2)], z: (k + j) * r, zTop: (k + j + 1) * r })
    }
    const sEnd = nT * td
    for (const sg of [1, -1]) sideLines.push({ pts: [at(0, sg * w / 2), at(sEnd, sg * w / 2)], z0, z1: (k + nT) * r })
    legs.push({ seg: i, a: P, u, n, s0: 0, s1: sEnd, kStart: k, kEnd: k + nT, z0, segLen: sEnd, w })
    if (i === 0) { chainL.push(at(0, w / 2)); chainR.push(at(0, -w / 2)) }
    usedPts.push(P)
    k += nT
    const E = at(sEnd, 0)
    // the riser up to the landing / the top floor
    riserLines.push({ pts: [at(sEnd, -w / 2), at(sEnd, w / 2)], z: k * r, zTop: (k + 1) * r })
    chainL.push(at(sEnd, w / 2)); chainR.push(at(sEnd, -w / 2))
    usedPts.push(E)
    if (i === solved.runs.length - 1) break

    // ---- the turn ----
    const t = solved.turns[i]
    const wo = wOf(i + 1)
    const sg = t.dir === 'left' ? 1 : -1
    const zL = (k + 1) * r
    const inner = sg === 1 ? chainL : chainR, outer = sg === 1 ? chainR : chainL
    const P2 = (s: number, c: number): V => add(add(E, mul(u, s)), mul(n, c))
    if (t.kind === 'winders') {
      const m = Math.max(2, Math.round(t.winders ?? 3))
      const wz = recipe.asBuilt && typeof t.winderOffsetFt === 'number' && t.winderOffsetFt >= 0
        ? winderGeometry(w, wo, sg, m, t.winderOffsetFt)
        : winderTurn(w, wo, sg, m)
      const Lc = (q: V): V => P2(q[0], q[1])
      wz.winders.forEach((poly, j) => winders.push({ poly: poly.map(Lc), zTop: (k + j + 1) * r, k: k + j }))
      wz.risers.forEach((seg, j) => riserLines.push({ pts: [Lc(seg[0]), Lc(seg[1])], z: (k + j + 1) * r, zTop: (k + j + 2) * r }))
      zones.push({ poly: wz.zone.map(Lc), q: Lc(wz.q), walk: wz.walk.map(Lc), k0: k, m, walkDepthFt: wz.walkDepth, narrowFt: wz.narrow })
      inner.push(...wz.innerPts.map(Lc))
      outer.push(...wz.outerPts.map(Lc))
      usedPts.push(...wz.walkPath.map(Lc))
      P = Lc(wz.nextStart)
      u = mul(n, sg)
      k += m
      continue
    }
    // an as-built stair keeps a landing as shallow as it was built (the code check says so);
    // only what the geometry needs is enforced
    const free = !!recipe.asBuilt
    if (t.angle === 0) {
      const min0 = free ? 0.5 : Math.max(w, wo, STAIR_MIN_LANDING_FT)
      const D = Math.max(t.depthFt ?? (free ? Math.max(w, wo, STAIR_MIN_LANDING_FT) : 0), min0)
      const hw = Math.max(w, wo) / 2
      landings.push({ poly: [P2(0, -hw), P2(D, -hw), P2(D, hw), P2(0, hw)], zTop: zL, seg: i, u, n, depth: D, minDepth: min0, turn: t.src, grip: 'near', c0: -hw, c1: hw, endAt: E })
      chainL.push(P2(0, hw), P2(D, hw), P2(D, wo / 2)); chainR.push(P2(0, -hw), P2(D, -hw), P2(D, -wo / 2))
      P = P2(D, 0)
      usedPts.push(P2(D / 2, 0))
    } else if (t.angle === 90) {
      const minD = free ? wo : Math.max(wo, STAIR_MIN_LANDING_FT)
      const D = Math.max(t.depthFt ?? Math.max(wo, STAIR_MIN_LANDING_FT), minD)
      landings.push({ poly: [P2(0, -w / 2), P2(D, -w / 2), P2(D, w / 2), P2(0, w / 2)], zTop: zL, seg: i, u, n, depth: D, minDepth: minD, turn: t.src, grip: 'near', c0: -w / 2, c1: w / 2, endAt: E })
      // the outgoing run leaves the landing's side, its far side flush with the landing's far edge
      inner.push(P2(D - wo, sg * w / 2))
      outer.push(P2(D, -sg * w / 2), P2(D, sg * w / 2))
      P = P2(D - wo / 2, sg * w / 2)
      usedPts.push(P2(D - wo / 2, 0))      // the walk line turns at the landing's middle
      u = mul(n, sg)
    } else {
      const minD = free ? 1 : Math.max(w, wo, STAIR_MIN_LANDING_FT)
      const D = Math.max(t.depthFt ?? Math.max(w, wo, STAIR_MIN_LANDING_FT), minD)
      const g = Math.max(0, t.gapFt ?? 4 * IN)
      const far = sg * (w / 2 + g + wo)
      landings.push({ poly: sg === 1
        ? [P2(0, -w / 2), P2(D, -w / 2), P2(D, far), P2(0, far)]
        : [P2(0, far), P2(D, far), P2(D, w / 2), P2(0, w / 2)], zTop: zL, seg: i, u, n, depth: D, minDepth: minD, turn: t.src, grip: 'far', c0: Math.min(sg === 1 ? -w / 2 : far, sg === 1 ? far : w / 2), c1: Math.max(sg === 1 ? -w / 2 : far, sg === 1 ? far : w / 2), endAt: E })
      inner.push(P2(0, sg * (w / 2 + g)))
      outer.push(P2(D, -sg * w / 2), P2(D, far), P2(0, far))
      P = P2(0, sg * (w / 2 + g + wo / 2))
      usedPts.push(P2(D / 2, 0), P2(D / 2, sg * (w / 2 + g + wo / 2)))
      u = mul(u, -1)
    }
    k += 1
  }

  // ---- place it: the top nosing on the anchor, the top run along `dir` ----
  const last = legs[legs.length - 1]
  const top = add(last.a, mul(last.u, last.s1))
  const dl = Math.hypot(dir[0], dir[1]) || 1
  const d: V = [dir[0] / dl, dir[1] / dl]
  const ang = Math.atan2(d[1], d[0]) - Math.atan2(last.u[1], last.u[0])
  const cs = Math.cos(ang), sn = Math.sin(ang)
  const rot = (p: V): V => [p[0] * cs - p[1] * sn, p[0] * sn + p[1] * cs]
  const X = (p: V): V => add(anchor, rot([p[0] - top[0], p[1] - top[1]]))
  const dd = (pl: V[]) => pl.filter((p, i) => i === 0 || Math.hypot(p[0] - pl[i - 1][0], p[1] - pl[i - 1][1]) > 1e-9)
  const left = dd(chainL.map(X)), right = dd(chainR.map(X))
  const ring = [...left, ...[...right].reverse()]
  const outline = ring.filter((p, i) => Math.hypot(p[0] - ring[(i + 1) % ring.length][0], p[1] - ring[(i + 1) % ring.length][1]) > 1e-9)
  return {
    calc,
    legs: legs.map((g, i) => ({ ...g, a: X(g.a), u: rot(g.u), n: rot(g.n), zEnd: i + 1 < legs.length ? legs[i + 1].z0 : calc.risers * r, top: i === legs.length - 1 })),
    treads: treads.map((t) => ({ ...t, quad: t.quad.map(X) })),
    landings: landings.map((l) => ({ ...l, poly: l.poly.map(X), u: l.u ? rot(l.u) : undefined, n: l.n ? rot(l.n) : undefined, endAt: l.endAt ? X(l.endAt) : undefined })),
    riserLines: riserLines.map((x) => ({ ...x, pts: [X(x.pts[0]), X(x.pts[1])] as [Pt2, Pt2] })),
    sideLines: sideLines.map((x) => ({ ...x, pts: [X(x.pts[0]), X(x.pts[1])] as [Pt2, Pt2] })),
    outline: outline.length >= 3 ? outline : null,
    edgeChains: [left, right],
    usedPts: usedPts.map(X),
    complete: true,
    ...(winders.length ? {
      winders: winders.map((x) => ({ ...x, poly: x.poly.map(X) })),
      winderZones: zones.map((z) => ({ ...z, poly: z.poly.map(X), q: X(z.q), walk: z.walk.map(X) })),
    } : {}),
  }
}

// ---------------------------------------------------------------------------
// WINDERS (Matt: "we use winders as space savers, especially in our small Laguna home projects")
//
// A quarter turn of m winders, in the incoming run's frame (s along it from its last riser, c
// across it; `sg` +1 turns left). The tread lines fan from a point Q set out past the inner
// corner by `e` both ways, so every winder has a real narrow end along the inner edges; the
// lines pass through equal divisions of the WALK LINE (12" in from the narrow side), so every
// winder is the same depth where it is walked. `e` is the least that meets the code (CRC
// R311.7.5.2.1): 10" at the walk line, 6" at the narrowest point. The turn eats `e` past the
// square in each direction.
// ---------------------------------------------------------------------------
const WALK_IN = 1                     // the walk line, 12" from the narrow end
const MIN_WALK = 10 * IN, MIN_NARROW = 6 * IN

function winderTurn(w: number, wo: number, sg: number, m: number) {
  const best = (e: number) => winderGeometry(w, wo, sg, m, e)
  let g = best(0)
  for (let e = 0; e <= 4; e += IN / 4) {
    g = best(e)
    if (g.walkDepth >= MIN_WALK - 1e-9 && g.narrow >= MIN_NARROW - 1e-9) break
  }
  return g
}

function winderGeometry(w: number, wo: number, sg: number, m: number, e: number) {
  const C = (s: number, c: number): V => [s, sg * c]          // c measured toward the turn
  const hw = w / 2
  // the turn's outline (counter-clockwise for a left turn)
  const zone: V[] = [C(0, -hw), C(e + wo, -hw), C(e + wo, hw + e), C(e, hw + e), C(e, hw), C(0, hw)]
  const inner: V[] = [C(0, hw), C(e, hw), C(e, hw + e)]
  const q = C(0, hw + e)
  // the walk line, WALK_IN in from the inner edges, entry to exit
  const wi = Math.min(WALK_IN, w / 2, wo / 2)
  const walkPts: V[] = [C(0, hw - wi), C(e + wi, hw - wi), C(e + wi, hw + e)]
  const seg = (a: V, b: V) => Math.hypot(b[0] - a[0], b[1] - a[1])
  const walkLen = seg(walkPts[0], walkPts[1]) + seg(walkPts[1], walkPts[2])
  const alongWalk = (d: number): V => {
    const l0 = seg(walkPts[0], walkPts[1])
    if (d <= l0) { const t = l0 ? d / l0 : 0; return [walkPts[0][0] + (walkPts[1][0] - walkPts[0][0]) * t, walkPts[0][1] + (walkPts[1][1] - walkPts[0][1]) * t] }
    const l1 = seg(walkPts[1], walkPts[2]), t = l1 ? (d - l0) / l1 : 0
    return [walkPts[1][0] + (walkPts[2][0] - walkPts[1][0]) * t, walkPts[1][1] + (walkPts[2][1] - walkPts[1][1]) * t]
  }
  const walk: V[] = Array.from({ length: m + 1 }, (_, j) => alongWalk((walkLen * j) / m))
  const dirs: V[] = walk.map((p) => { const d: V = [p[0] - q[0], p[1] - q[1]]; const l = Math.hypot(d[0], d[1]) || 1; return [d[0] / l, d[1] / l] })
  const cross = (a: V, b: V) => a[0] * b[1] - a[1] * b[0]
  /** keep the part of `poly` on the side of the line Q + t·d that `keep` is on */
  const clip = (poly: V[], d: V, keep: V): V[] => {
    const side = (x: V) => cross(d, [x[0] - q[0], x[1] - q[1]])
    const ks = Math.sign(side(keep)) || 1
    const out: V[] = []
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i], b = poly[(i + 1) % poly.length]
      const sa = side(a) * ks, sb = side(b) * ks
      if (sa >= -1e-12) out.push(a)
      if ((sa > 1e-12 && sb < -1e-12) || (sa < -1e-12 && sb > 1e-12)) {
        const t = sa / (sa - sb)
        out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
      }
    }
    return out
  }
  const winders: V[][] = []
  for (let j = 0; j < m; j++) {
    let poly = clip(zone, dirs[j], walk[j + 1])
    poly = clip(poly, dirs[j + 1], walk[j])
    winders.push(poly.filter((p, i) => seg(p, poly[(i + 1) % poly.length]) > 1e-9))
  }
  /** where the line Q + t·d crosses a polyline: the nearest and farthest hits */
  const hits = (pl: V[], d: V, closed: boolean): number[] => {
    const ts: number[] = []
    const nSeg = closed ? pl.length : pl.length - 1
    for (let i = 0; i < nSeg; i++) {
      const a = pl[i], b = pl[(i + 1) % pl.length]
      const e1: V = [b[0] - a[0], b[1] - a[1]]
      const den = cross(d, e1)
      if (Math.abs(den) < 1e-12) continue
      const aq: V = [a[0] - q[0], a[1] - q[1]]
      const t = cross(aq, e1) / den, v = cross(aq, d) / den
      if (v >= -1e-9 && v <= 1 + 1e-9 && t > 1e-9) ts.push(t)
    }
    return ts.sort((x, y) => x - y)
  }
  // the riser faces between winders: each inside tread line, across the turn
  const risers: [V, V][] = []
  for (let j = 1; j < m; j++) {
    const ts = hits(zone, dirs[j], true)
    if (ts.length >= 2) risers.push([[q[0] + dirs[j][0] * ts[0], q[1] + dirs[j][1] * ts[0]], [q[0] + dirs[j][0] * ts[ts.length - 1], q[1] + dirs[j][1] * ts[ts.length - 1]]])
  }
  // the narrow ends: each winder's share of the inner edges
  const innerAt = (d: V): number => {
    let acc = 0
    for (let i = 0; i + 1 < inner.length; i++) {
      const ts = hits([inner[i], inner[i + 1]], d, false)
      if (ts.length) { const p: V = [q[0] + d[0] * ts[0], q[1] + d[1] * ts[0]]; return acc + seg(inner[i], p) }
      acc += seg(inner[i], inner[i + 1])
    }
    return d[0] * sg > 0.999 || Math.abs(d[0]) > 0.999 ? acc : 0
  }
  const at = dirs.map((d, j) => (j === 0 ? 0 : j === m ? 2 * e : innerAt(d)))
  let narrow = Infinity
  for (let j = 0; j < m; j++) narrow = Math.min(narrow, at[j + 1] - at[j])
  return {
    zone, winders, risers, q, walk,
    walkDepth: walkLen / m, narrow,
    innerPts: [C(e, hw), C(e, hw + e)],
    outerPts: [C(e + wo, -hw), C(e + wo, hw + e)],
    walkPath: [C(e + wo / 2, 0)],     // the centreline turns at the turn's middle
    nextStart: C(e + wo / 2, hw + e),
  }
}


// ---------------------------------------------------------------------------
// BRANCHING stairs — split (T), imperial and bifurcated (bim-plan 33 §2, #13 / #14)
//
// One walking path is solved like any two-run stair (main + landing + arm, or arm + landing + main),
// and the arm is laid twice, mirrored. The landing spans both arms. The two guard sides are the
// stair's walker-left and walker-right edges (up the main run and on up the LEFT arm, or the right);
// the landing's far edge and the arms' other sides make a third edge (`backChain`), left for a wall
// or a third guard.
// ---------------------------------------------------------------------------
function branchLayout(recipe: StairRecipe, anchor: Pt2, dir: Pt2, opts: StairLayoutOpts): StairLayout | null {
  const calc = stairRun(opts.floorToFloorFt, opts.maxRiserFt, opts.treadDepthFt)
  const r = calc.riserFt, td = opts.treadDepthFt
  const join = recipe.branch === 'join'
  const t = recipe.turns[0]
  const half = t.angle === 180
  // the path: [main, arm] for a split, [arm, main] for a join — never winders, never an auto split
  const solved = solveStairRecipe({ ...recipe, turns: [{ ...t, kind: 'landing' }] }, calc.treads, r)
  if (solved.runs.length !== 2) return null           // a flight past 12'-7" splits — not for branches yet
  const W0 = opts.widthFt
  const iMain = join ? 1 : 0, iArm = join ? 0 : 1
  const nMain = solved.runs[iMain].treads, nArm = solved.runs[iArm].treads
  const wm = solved.runs[iMain].widthFt || W0, wa = solved.runs[iArm].widthFt || W0
  const g = Math.max(0, t.gapFt ?? 4 * IN)
  const legs: StairLayoutLeg[] = []
  const treads: StairLayout['treads'] = []
  const riserLines: StairLayout['riserLines'] = []
  const sideLines: StairLayout['sideLines'] = []
  /** one straight run from P climbing u, nT treads from position k */
  const run = (P: V, u: V, w: number, nT: number, k: number, zEnd: number, top: boolean, seg: number) => {
    const n = leftN(u)
    const at = (s: number, c: number): V => add(add(P, mul(u, s)), mul(n, c))
    for (let j = 0; j < nT; j++) {
      const sF = j * td
      treads.push({ i: k + j, quad: [at(sF, -w / 2), at(sF + td, -w / 2), at(sF + td, w / 2), at(sF, w / 2)], zTop: (k + j + 1) * r })
      riserLines.push({ pts: [at(sF, -w / 2), at(sF, w / 2)], z: (k + j) * r, zTop: (k + j + 1) * r })
    }
    const sEnd = nT * td
    riserLines.push({ pts: [at(sEnd, -w / 2), at(sEnd, w / 2)], z: (k + nT) * r, zTop: (k + nT + 1) * r })
    for (const sg of [1, -1]) sideLines.push({ pts: [at(0, sg * w / 2), at(sEnd, sg * w / 2)], z0: k * r, z1: (k + nT) * r })
    legs.push({ seg, a: P, u, n, s0: 0, s1: sEnd, kStart: k, kEnd: k + nT, z0: k * r, segLen: sEnd, w, zEnd, top })
    return { at, sEnd }
  }
  // local frame: the landing's near edge centre at the origin, the main run's direction +x
  const D = recipe.asBuilt
    ? Math.max(t.depthFt ?? (half ? Math.max(wm, wa, 3) : Math.max(wa, 3)), half ? 1 : wa)
    : Math.max(t.depthFt ?? 0, half ? Math.max(wm, wa, 3) : Math.max(wa, 3))
  const u: V = [1, 0], n: V = [0, 1]
  const P2 = (s: number, c: number): V => [s, c]
  let landing: V[]
  const chainL: V[] = [], chainR: V[] = [], back: V[] = []
  const usedPts: V[] = []
  const armOff = half ? wm / 2 + g + wa / 2 : 0
  if (!join) {
    // main climbs to the landing's near edge; arms leave its sides (90°) or come back (180°)
    const kL = nMain                                         // the landing's position
    const mainP: V = [-nMain * td, 0]
    const m = run(mainP, u, wm, nMain, 0, (kL + 1) * r, false, 0)
    const zA = kL + 1
    chainL.push(m.at(0, wm / 2), m.at(nMain * td, wm / 2)); chainR.push(m.at(0, -wm / 2), m.at(nMain * td, -wm / 2))
    usedPts.push(mainP, P2(0, 0))
    if (!half) {
      landing = [P2(0, -wm / 2), P2(D, -wm / 2), P2(D, wm / 2), P2(0, wm / 2)]
      const aL = run(P2(D - wa / 2, wm / 2), n, wa, nArm, zA, calc.risers * r, true, 1)
      const aR = run(P2(D - wa / 2, -wm / 2), [0, -1], wa, nArm, zA, calc.risers * r, true, 2)
      // walker-left: up the main, on up the left arm's near (downhill) side
      chainL.push(P2(D - wa, wm / 2), aL.at(aL.sEnd, wa / 2))
      chainR.push(P2(D - wa, -wm / 2), aR.at(aR.sEnd, -wa / 2))
      back.push(aL.at(aL.sEnd, -wa / 2), P2(D, wm / 2), P2(D, -wm / 2), aR.at(aR.sEnd, wa / 2))
      usedPts.push(P2(D - wa / 2, 0), aL.at(aL.sEnd, 0))
    } else {
      const far = wm / 2 + g + wa
      landing = [P2(0, -far), P2(D, -far), P2(D, far), P2(0, far)]
      const aL = run(P2(0, armOff), [-1, 0], wa, nArm, zA, calc.risers * r, true, 1)
      const aR = run(P2(0, -armOff), [-1, 0], wa, nArm, zA, calc.risers * r, true, 2)
      // walker-left up the left arm (travelling back) is its side toward the main run
      // (an arm heading back has its left normal pointing away from the left arm's main-run side:
      // its inner, well side is +wa/2 for the left arm and −wa/2 for the right)
      chainL.push(P2(0, wm / 2 + g), aL.at(aL.sEnd, wa / 2))
      chainR.push(P2(0, -(wm / 2 + g)), aR.at(aR.sEnd, -wa / 2))
      back.push(aL.at(aL.sEnd, -wa / 2), P2(0, far), P2(D, far), P2(D, -far), P2(0, -far), aR.at(aR.sEnd, wa / 2))
      usedPts.push(P2(D / 2, 0), P2(D / 2, armOff), aL.at(aL.sEnd, 0))
    }
  } else {
    // two arms climb to the landing; the main run climbs on from its far edge (90°) or back
    // between the arms (180°)
    const kL = nArm
    if (!half) {
      landing = [P2(0, -wm / 2), P2(D, -wm / 2), P2(D, wm / 2), P2(0, wm / 2)]
      // arms arrive at the landing's sides, their far sides flush with its near edge
      const aL = run(P2(wa / 2, wm / 2 + nArm * td), [0, -1], wa, nArm, 0, (kL + 1) * r, false, 0)
      const aR = run(P2(wa / 2, -(wm / 2 + nArm * td)), [0, 1], wa, nArm, 0, (kL + 1) * r, false, 1)
      const mn = run(P2(D, 0), u, wm, nMain, kL + 1, calc.risers * r, true, 2)
      // walker-left: up the left arm (heading −n, its left is +u... the landing side), on up the main
      chainL.push(aL.at(0, wa / 2), aL.at(aL.sEnd, wa / 2), P2(D, wm / 2), mn.at(mn.sEnd, wm / 2))
      chainR.push(aR.at(0, -wa / 2), aR.at(aR.sEnd, -wa / 2), P2(D, -wm / 2), mn.at(mn.sEnd, -wm / 2))
      back.push(aL.at(0, -wa / 2), aL.at(aL.sEnd, -wa / 2), P2(0, 0), aR.at(aR.sEnd, wa / 2), aR.at(0, wa / 2))
      usedPts.push(aL.at(0, 0), P2(wa / 2, wm / 2), P2(D / 2, 0), mn.at(mn.sEnd, 0))
    } else {
      // two arms coming back to one landing has no single left and right edge to guard — the
      // imperial stair (a split) is the grand half-turn; a join turns 90° only
      return null
    }
  }
  const landingsLocal: StairLayout['landings'] = [{ poly: landing!, zTop: (join ? nArm + 1 : nMain + 1) * r, u, n, depth: D, minDepth: D }]
  // ---- place it: a join on its main run's top step; a split on the midpoint of its arms' ----
  const tops = legs.filter((x) => x.top)
  const topPts = tops.map((x) => add(x.a, mul(x.u, x.s1)))
  const top: V = [topPts.reduce((a, p) => a + p[0], 0) / topPts.length, topPts.reduce((a, p) => a + p[1], 0) / topPts.length]
  const faceU = join ? tops[0].u : u                         // which way the stair faces
  const dl = Math.hypot(dir[0], dir[1]) || 1
  const ang = Math.atan2(dir[1] / dl, dir[0] / dl) - Math.atan2(faceU[1], faceU[0])
  const cs = Math.cos(ang), sn = Math.sin(ang)
  const rot = (p: V): V => [p[0] * cs - p[1] * sn, p[0] * sn + p[1] * cs]
  const X = (p: V): V => add(anchor, rot([p[0] - top[0], p[1] - top[1]]))
  // the footprint: left edge up, across the top(s) and the back, right edge down
  const dd = (pl: V[]) => pl.filter((p, i) => i === 0 || Math.hypot(p[0] - pl[i - 1][0], p[1] - pl[i - 1][1]) > 1e-9)
  const ring = dd((join ? [...chainL, ...[...chainR].reverse(), ...[...back].reverse()] : [...chainL, ...back, ...[...chainR].reverse()]).map(X))
  const outline = ring.filter((p, i) => Math.hypot(p[0] - ring[(i + 1) % ring.length][0], p[1] - ring[(i + 1) % ring.length][1]) > 1e-9)
  return {
    calc,
    legs: legs.map((x) => ({ ...x, a: X(x.a), u: rot(x.u), n: rot(x.n) })),
    treads: treads.map((x) => ({ ...x, quad: x.quad.map(X) })),
    landings: landingsLocal.map((l) => ({ ...l, poly: l.poly.map(X), u: rot(l.u!), n: rot(l.n!) })),
    riserLines: riserLines.map((x) => ({ ...x, pts: [X(x.pts[0]), X(x.pts[1])] as [Pt2, Pt2] })),
    sideLines: sideLines.map((x) => ({ ...x, pts: [X(x.pts[0]), X(x.pts[1])] as [Pt2, Pt2] })),
    outline: outline.length >= 3 ? outline : null,
    edgeChains: [dd(chainL.map(X)), dd(chainR.map(X))],
    backChain: dd(back.map(X)),
    usedPts: usedPts.map(X),
    complete: true,
  }
}
